import { FC, ReactElement, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import { SelectInput } from '../../../../../compositions';
import { SSRVariantList } from '../../../../../compositions/SSRVariantList/SSRVariantList';
import { FormImageOption } from '../../../../../entities/@forms/Form/Form';
import { AppRoute, appRoutes } from '../../../../../entities/Routing/Routing';
import { isSSR } from '../../../../../helpers';
import { replaceUrlParamKeysWithValues } from '../../../../../helpers/url';
import { useTrans } from '../../../../../hooks';

import './ProductVariantSelector.scss';

interface ProductVariantSelectorProps {
    variants: FormImageOption[];
    currentSlug: string;
    className?: string;
}

export const ProductVariantSelector: FC<ProductVariantSelectorProps> = ({
    variants,
    currentSlug,
    className = '',
}): ReactElement => {
    const trans = useTrans();
    const navigate = useNavigate();

    const sortedVariants = useMemo((): FormImageOption[] => [...variants].sort((a, b) => a.label.localeCompare(b.label)), [variants]);

    const currentVariant = variants.find(option => (
        option.value === currentSlug
    )) || variants[0];

    const handleVariantsDropdown = (value: string): void => {
        const productDetailPath = trans(appRoutes[AppRoute.productDetail].path);
        const productDetailUrl = replaceUrlParamKeysWithValues(productDetailPath, {
            slug: value,
        });

        navigate(productDetailUrl);
    };

    return (
        <div className={`product-variant-selector ${className}`}>
            <p className="product-variant-selector__name">
                <span>
                    {trans('containers.productDetailRenderer.availableIn')}
                </span>

                <span className="product-variant-selector__variants-amount">
                    {trans('containers.productDetailRenderer.variantsCount', { amount: variants.length })}
                </span>
            </p>

            {isSSR && (
                <SSRVariantList variants={variants} />
            )}

            <SelectInput
                hideLabel
                required
                name="productVariantSelector"
                options={sortedVariants}
                label={trans('containers.productDetailRenderer.variants')}
                value={currentVariant}
                onChange={handleVariantsDropdown}
                className="product-variant-selector__dropdown"
            />
        </div>
    );
};
